import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  DataTable,
  TableContainer,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TableSelectRow,
  TableBatchActions,
  TableBatchAction,
  Button,
  Modal,
  TextInput,
  Pagination,
  InlineNotification,
  OverflowMenu,
  OverflowMenuItem,
  Dropdown,
  InlineLoading,
  Form,
  MultiSelect,
} from "@carbon/react";
import { Add, Delete, Settings, UserAvatar, Upload } from "@carbon/icons-react";
import { callApi } from "../../../services/apiService";
import { useUser } from "../../../context/UserContext";

// Updated headers
const headers = [
  { key: "name", header: "Name" },
  { key: "userName", header: "Username" },
  { key: "email", header: "Email" },
  { key: "contact", header: "Contact Number" },
  { key: "role", header: "Role" },
  { key: "status", header: "Status" },
  { key: "createdBy", header: "Created By" },
  { key: "profilePic", header: "Profile Picture" },
];

// Password strength regex
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
const isPasswordStrong = (password) => PASSWORD_REGEX.test(password);

// Username minimum length
const USERNAME_MIN_LENGTH = 8;

// Profile picture constants
const MAX_PROFILE_PIC_SIZE_MB = 5;
const ACCEPTED_IMAGE_TYPES = "image/jpeg,image/jpg,image/png,image/gif,image/webp";

// Helper function to safely convert any value to string
const safeToString = (value) => {
  if (value === null || value === undefined) return "-";
  if (typeof value === 'object') {
    if (value?.name) return value.name;
    if (value?.username) return value.username;
    if (value?.user_id) return `User ID: ${value.user_id}`;
    return JSON.stringify(value);
  }
  return String(value);
};

export default function UserProfiles() {
  const { user } = useUser();
  const isVerifier = user?.role_name === "VERIFIER";
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    userName: "",
    name: "",
    email: "",
    contact: "",
    roleId: "",
  });
  
  // Profile picture state
  const [profilePicFile, setProfilePicFile] = useState(null);
  const [profilePicPreview, setProfilePicPreview] = useState("");
  const [uploadingProfilePic, setUploadingProfilePic] = useState(false);
  
  const [errors, setErrors] = useState({});
  const [editingProfile, setEditingProfile] = useState(null);
  const [deleteProfileId, setDeleteProfileId] = useState(null);
  const [passwordUser, setPasswordUser] = useState(null);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // filters
   const [selectedStatus, setSelectedStatus] = useState([]);
const [selectedRoleFilter, setSelectedRoleFilter] = useState([]);
const statusList = [
  { id: "active", text: "Active" },
  { id: "cancelled", text: "Cancelled" },
];

const [searchTerm, setSearchTerm] = useState("");
const [debouncedSearch, setDebouncedSearch] = useState("");
  
  const [notification, setNotification] = useState({ 
    show: false, 
    title: "", 
    message: "", 
    kind: "success" 
  });

  const [checking, setChecking] = useState({
    userName: false,
    email: false,
    contact: false,
  });

  const [unique, setUnique] = useState({
    userName: true,
    email: true,
    contact: true,
  });

  const [touched, setTouched] = useState({
    userName: false,
    name: false,
    email: false,
    contact: false,
    roleId: false,
  });

const [passwordForm, setPasswordForm] = useState({
  username: "",
  new_password: "",
});

const [passwordErrors, setPasswordErrors] = useState({});
const [passwordApiError, setPasswordApiError] = useState("");
const [sameAsOldPasswordError, setSameAsOldPasswordError] = useState(""); // <-- ADD THIS

  // Batch delete state
  const [batchDeleteIds, setBatchDeleteIds] = useState([]);

  // Role management state
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  // Pagination and Search states
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  

  const debounceRef = useRef({});
  const notificationTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);


  // Helper function to extract creator name
  const extractCreatorName = useCallback((createdByObj) => {
    if (!createdByObj) return "-";
    if (typeof createdByObj === 'object') {
      return createdByObj?.name || createdByObj?.username || `User ${createdByObj?.user_id || ''}`;
    }
    return safeToString(createdByObj);
  }, []);

const normalizeUserProfile = useCallback((item) => {
  const roleName = item?.role_name || item?.role?.name || "-";
  const profilePic = item?.profile_pic || item?.profilePic || "";

  return {
    id: safeToString(item?.user_profile_id || item?.id || 0), 
    userId: (item?.user_id), 
    name: safeToString(item?.name),
    email: safeToString(item?.email),
    userName: safeToString(item?.username),
    contact: safeToString(item?.contact_number || item?.contact),
    role: safeToString(roleName),
    roleId: safeToString(item?.role_id || item?.roleId || ""),
    isGoogleProvider:
  item?.is_google_provider === true ||
  item?.is_google_provider === 1 ||
  item?.is_google_provider === "true",
    status: safeToString(item?.status) || "Active",
    createdBy: extractCreatorName(item?.created_by),
    profilePic: profilePic,
  };
}, [extractCreatorName]);

  // Show notification helper
  const showNotification = useCallback((title, message, kind = "success") => {
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
    }
    
    setNotification({ show: true, title, message, kind });
    
    notificationTimeoutRef.current = setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, 5000);
  }, []);

  // Clear profile picture
  const clearProfilePic = useCallback(() => {
    setProfilePicFile(null);
    setProfilePicPreview("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  // Fetch roles
  const fetchRoles = useCallback(async () => {
    try {
      setLoadingRoles(true);
      const response = await callApi("GET", "/user-roles/");
      
      if (response?.data && Array.isArray(response.data)) {
        const rolesData = response.data.map(role => ({
          id: safeToString(role?.id || role?.ROLE_ID),
          text: safeToString(role?.role_name || role?.ROLE_NAME),
        }));
        setRoles(rolesData);
      }
    } catch {
      showNotification("Error", "Failed to load roles", "error");
    } finally {
      setLoadingRoles(false);
    }
  }, [showNotification]);

 

  // Fetch user profiles
const fetchUserProfiles = useCallback(
  async (status = "", role = "", filter = false) => {
    try {
      if (filter) {
  setIsFiltering(true);
} else {
  setIsLoading(true);
}

      const params = new URLSearchParams();

      if (status) {
        params.append("status", status);
      }

      if (role) {
        params.append("role_name", role);
      }

      const url =
        params.toString().length > 0
          ? `/user-profiles/?${params.toString()}`
          : "/user-profiles/";

      const response = await callApi("GET", url);
    

      if (response?.data && Array.isArray(response.data)) {
        const formattedRows = response.data.map(normalizeUserProfile);

        setRows(formattedRows);
        setFilteredRows(formattedRows);
      }
    } catch {
      showNotification("Error", "Failed to load user profiles", "error");
    } finally {
      if (filter) {
  setIsFiltering(false);
} else {
  setIsLoading(false);
}
    }
  },
  [normalizeUserProfile, showNotification]
);
const handleStatusChange = ({ selectedItems }) => {
  setSelectedStatus(selectedItems);
};

const handleRoleChange = ({ selectedItems }) => {
  setSelectedRoleFilter(selectedItems);
};

useEffect(() => {
  let isMounted = true;

  const debounceTimers = debounceRef.current;

const fetchData = async () => {
  if (!isMounted) return;

  await fetchRoles();

  await fetchUserProfiles(
    selectedStatus.length ? selectedStatus[0].id : "",
    selectedRoleFilter.length ? selectedRoleFilter[0].text : "",
    true
  );
};

  fetchData();

  return () => {
    isMounted = false;

    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
    }

    Object.keys(debounceTimers).forEach((key) => {
      if (debounceTimers[key]) {
        clearTimeout(debounceTimers[key]);
      }
    });
  };
}, [fetchRoles, fetchUserProfiles]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim().toLowerCase());
    }, 350);
    
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Filter rows based on search
useEffect(() => {
  let filtered = [...rows];

  if (debouncedSearch) {
    const search = debouncedSearch.toLowerCase();

    filtered = filtered.filter((row) =>
      row.name?.toLowerCase().includes(search) ||
      row.userName?.toLowerCase().includes(search) ||
      row.email?.toLowerCase().includes(search) ||
      row.contact?.toLowerCase().includes(search) ||
      row.role?.toLowerCase().includes(search) ||
      row.status?.toLowerCase().includes(search)
    );
  }



  setFilteredRows(filtered);
  setPage(1);
}, [debouncedSearch, rows,]);

  // Reset form
  const resetForm = useCallback(() => {
    setFormData({
      userName: "",
      name: "",
      email: "",
      contact: "",
      roleId: "",
    });
    setErrors({});
    setUnique({
      userName: true,
      email: true,
      contact: true,
    });
    setTouched({
      userName: false,
      name: false,
      email: false,
      contact: false,
      roleId: false,
    });
    setChecking({
      userName: false,
      email: false,
      contact: false,
    });
    setIsEdit(false);
    setEditingProfile(null);
    clearProfilePic();
  }, [clearProfilePic]);

  // Handle add new
  const handleAddNew = useCallback(() => {
    resetForm();
    setIsModalOpen(true);
  }, [resetForm]);

  // Handle profile picture change
  const handleProfilePicChange = useCallback((e) => {
    const file = e.target.files?.[0];
    
    if (!file) return;
    
    if (file.size > MAX_PROFILE_PIC_SIZE_MB * 1024 * 1024) {
      setErrors(prev => ({
        ...prev,
        profilePic: `Profile picture exceeds ${MAX_PROFILE_PIC_SIZE_MB}MB limit`
      }));
      return;
    }
    
    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({
        ...prev,
        profilePic: "Please select a valid image file"
      }));
      return;
    }
    
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.profilePic;
      return newErrors;
    });
    
    setProfilePicFile(file);
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfilePicPreview(reader.result);
    };
    reader.readAsDataURL(file);
  }, []);

  // Check unique field
  const checkUniqueField = useCallback(async (field, value) => {
    // For edit mode: if the value matches the current editing profile's value, it's valid
    if (isEdit && editingProfile) {
      const currentValue = editingProfile[field]?.toString().toLowerCase();
      const newValue = value.toString().toLowerCase();
      
      if (newValue === currentValue) {
        setUnique(prev => ({ ...prev, [field]: true }));
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
        return;
      }
    }

    if (!value || value.toString().trim() === "") {
      setUnique(prev => ({ ...prev, [field]: true }));
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
      return;
    }

    setChecking(prev => ({ ...prev, [field]: true }));

    try {
      const trimmedValue = value.toString().trim().toLowerCase();
      
      const existing = rows.some(row => {
        if (isEdit && editingProfile && row.id === editingProfile.id) {
          return false;
        }
        const rowValue = row[field]?.toString().toLowerCase();
        return rowValue === trimmedValue;
      });
      
      const isUnique = !existing;
      
      setUnique(prev => ({ ...prev, [field]: isUnique }));

      if (!isUnique) {
        setErrors(prev => ({
          ...prev,
          [field]: `${field === 'userName' ? 'Username' : field === 'contact' ? 'Contact number' : 'Email'} already exists`,
        }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    } catch {
      setUnique(prev => ({ ...prev, [field]: true }));
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    } finally {
      setChecking(prev => ({ ...prev, [field]: false }));
    }
  }, [isEdit, editingProfile, rows]);

  // Handle input change
  const handleInputChange = useCallback((e) => {
    const { id, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [id]: value,
    }));

    setTouched(prev => ({
      ...prev,
      [id]: true,
    }));

    if (errors[id]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[id];
        return newErrors;
      });
    }

    if (id === "name" && !value.trim()) {
      setErrors(prev => ({ ...prev, name: "Name is required" }));
    }

    if (id === "userName") {
      if (!value.trim()) {
        setErrors(prev => ({ ...prev, userName: "Username is required" }));
      } else if (value.trim().length < USERNAME_MIN_LENGTH) {
        setErrors(prev => ({ ...prev, userName: `Username must be at least ${USERNAME_MIN_LENGTH} characters` }));
      }
    }

    if (id === "email" && value.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value.trim())) {
        setErrors(prev => ({ ...prev, email: "Invalid email format" }));
      }
    }

    if (id === "contact" && value.trim()) {
      const contactRegex = /^[0-9+\-\s()]{10,15}$/;
      if (!contactRegex.test(value.trim())) {
        setErrors(prev => ({ ...prev, contact: "Invalid contact number" }));
      }
    }

    if (["userName", "email", "contact"].includes(id)) {
      if (debounceRef.current[id]) {
        clearTimeout(debounceRef.current[id]);
      }

      debounceRef.current[id] = setTimeout(() => {
        checkUniqueField(id, value.trim());
      }, 500);
    }
  }, [checkUniqueField, errors]);

  // Handle password input change
const handlePasswordInputChange = useCallback((e) => {
  const { id, value } = e.target;

  setPasswordApiError("");
  setSameAsOldPasswordError(""); 
  setIsPasswordModalOpen(true);// Clear this error when user types

  setPasswordForm(prev => ({ ...prev, [id]: value }));

  // Validate new password strength
  if (id === "new_password") {
    if (value && !isPasswordStrong(value)) {
      setPasswordErrors(prev => ({
        ...prev,
        new_password:
          "Password must contain uppercase, lowercase, number, special character and be at least 8 characters",
      }));
    } else {
      setPasswordErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.new_password;
        return newErrors;
      });
    }

    // ALSO check mismatch when typing new password
    if (passwordForm.confirm_password && value !== passwordForm.confirm_password) {
      setPasswordErrors(prev => ({
        ...prev,
        confirm_password: "Passwords do not match",
      }));
    } else {
      setPasswordErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.confirm_password;
        return newErrors;
      });
    }
  }

  // Validate confirm password
  if (id === "confirm_password") {
    if (value !== passwordForm.new_password) {
      setPasswordErrors(prev => ({
        ...prev,
        confirm_password: "Passwords do not match",
      }));
    } else {
      setPasswordErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.confirm_password;
        return newErrors;
      });
    }
  }
}, [passwordForm.new_password, passwordForm.confirm_password]);

  // Validate password form
const validatePasswordForm = useCallback(() => {
  const errs = {};

  if (!passwordForm.new_password.trim()) {
    errs.new_password = "Password is required";
  } else if (!isPasswordStrong(passwordForm.new_password)) {
    errs.new_password =
      "Password must contain uppercase, lowercase, number, special character and be at least 8 characters";
  }

  if (!passwordForm.confirm_password.trim()) {
    errs.confirm_password = "Confirm password is required";
  } else if (passwordForm.new_password !== passwordForm.confirm_password) {
    errs.confirm_password = "Passwords do not match";
  }

  setPasswordErrors(errs);
  return Object.keys(errs).length === 0;
}, [passwordForm]);

  // Handle blur
  const handleBlur = useCallback((field) => {
    setTouched(prev => ({
      ...prev,
      [field]: true,
    }));
    
    const value = formData[field];
    
    if (!value || value.toString().trim() === "") {
      const requiredFields = ['name', 'userName', 'email', 'contact', 'roleId'];
      if (requiredFields.includes(field)) {
        setErrors(prev => ({ 
          ...prev, 
          [field]: `${field === 'userName' ? 'Username' : field === 'contact' ? 'Contact number' : field} is required` 
        }));
      }
      return;
    }
    
    const trimmedValue = value.toString().trim();
    let error = "";

    switch (field) {
      case "name":
        if (!trimmedValue) error = "Name is required";
        break;
      case "userName":
        if (!trimmedValue) {
          error = "Username is required";
        } else if (trimmedValue.length < USERNAME_MIN_LENGTH) {
          error = `Username must be at least ${USERNAME_MIN_LENGTH} characters`;
        } else if (!isEdit && !unique.userName && !checking.userName) {
          // Only check uniqueness for new users
          error = "Username already exists";
        }
        break;
      case "email":
        if (!trimmedValue) {
          error = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedValue)) {
          error = "Invalid email format";
        } else {
          // For edit mode: only check uniqueness if email has changed
          if (isEdit && editingProfile) {
            if (trimmedValue.toLowerCase() !== editingProfile.email?.toLowerCase() && 
                !unique.email && !checking.email) {
              error = "Email already exists";
            }
          } else if (!isEdit && !unique.email && !checking.email) {
            // For new users: always check uniqueness
            error = "Email already exists";
          }
        }
        break;
      case "contact":
        if (!trimmedValue) {
          error = "Contact number is required";
        } else if (!/^[0-9+\-\s()]{10,15}$/.test(trimmedValue)) {
          error = "Invalid contact number";
        } else {
          // For edit mode: only check uniqueness if contact has changed
          if (isEdit && editingProfile) {
            if (trimmedValue.toLowerCase() !== editingProfile.contact?.toLowerCase() && 
                !unique.contact && !checking.contact) {
              error = "Contact number already exists";
            }
          } else if (!isEdit && !unique.contact && !checking.contact) {
            // For new users: always check uniqueness
            error = "Contact number already exists";
          }
        }
        break;
      case "roleId":
        if (!trimmedValue) error = "Role is required";
        break;
      default:
        break;
    }

    if (error) {
      setErrors(prev => ({ ...prev, [field]: error }));
    } else if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [formData, unique, checking, errors, isEdit, editingProfile]);

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Name is required";
    
    if (!formData.userName.trim()) {
      newErrors.userName = "Username is required";
    } else if (formData.userName.trim().length < USERNAME_MIN_LENGTH) {
      newErrors.userName = `Username must be at least ${USERNAME_MIN_LENGTH} characters`;
    }
    
    if (!formData.email.trim()) newErrors.email = "Email is required";
    if (!formData.contact.trim()) newErrors.contact = "Contact number is required";
    if (!formData.roleId) newErrors.roleId = "Role is required";

    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = "Invalid email format";
    }

    if (formData.contact.trim() && !/^[0-9+\-\s()]{10,15}$/.test(formData.contact.trim())) {
      newErrors.contact = "Invalid contact number";
    }

    if (checking.userName || checking.email || checking.contact) {
      showNotification("Validation", "Please wait while we check field availability", "info");
      return false;
    }

    // For edit mode
    if (isEdit && editingProfile) {
      // Check if email changed and is unique
      if (formData.email.trim().toLowerCase() !== editingProfile.email?.toLowerCase()) {
        if (!unique.email) {
          newErrors.email = "Email already exists";
        }
      }
      
      // Check if contact changed and is unique
      if (formData.contact.trim().toLowerCase() !== editingProfile.contact?.toLowerCase()) {
        if (!unique.contact) {
          newErrors.contact = "Contact number already exists";
        }
      }
    } else {
      // For new users, check all uniqueness
      if (!unique.userName) {
        newErrors.userName = "Username already exists";
      }
      if (!unique.email) {
        newErrors.email = "Email already exists";
      }
      if (!unique.contact) {
        newErrors.contact = "Contact number already exists";
      }
    }

    setErrors(newErrors);

    // Check form validity
    const hasNoErrors = Object.keys(newErrors).length === 0;
    
    // For edit mode, we don't need to check username uniqueness (can't change)
    if (isEdit && editingProfile) {
      const emailValid = formData.email.trim().toLowerCase() === editingProfile.email?.toLowerCase() || unique.email;
      const contactValid = formData.contact.trim().toLowerCase() === editingProfile.contact?.toLowerCase() || unique.contact;
      
      return hasNoErrors && 
             emailValid && 
             contactValid &&
             !checking.email && 
             !checking.contact;
    }
    
    // For new users
    return hasNoErrors && 
           unique.userName && 
           unique.email && 
           unique.contact &&
           !checking.userName && 
           !checking.email && 
           !checking.contact;
  }, [formData, unique, checking, isEdit, editingProfile, showNotification]);

  // Check if form is valid
  const isFormValid = useMemo(() => {
    const hasAllRequiredFields = 
      formData.name.trim() && 
      formData.userName.trim() && 
      formData.email.trim() && 
      formData.contact.trim() && 
      formData.roleId;
    
    if (!hasAllRequiredFields) return false;
    
    if (formData.userName.trim().length < USERNAME_MIN_LENGTH) return false;
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) return false;
    
    if (!/^[0-9+\-\s()]{10,15}$/.test(formData.contact.trim())) return false;
    
    if (checking.userName || checking.email || checking.contact) return false;
    
    if (errors.name || errors.userName || errors.email || errors.contact || errors.roleId) return false;
    
    // For edit mode
    if (isEdit && editingProfile) {
      // Check if email changed and is unique
      const emailChanged = formData.email.trim().toLowerCase() !== editingProfile.email?.toLowerCase();
      const emailValid = !emailChanged || unique.email;
      
      // Check if contact changed and is unique
      const contactChanged = formData.contact.trim().toLowerCase() !== editingProfile.contact?.toLowerCase();
      const contactValid = !contactChanged || unique.contact;
      
      return emailValid && contactValid;
    }
    
    // For new users
    return unique.userName && unique.email && unique.contact;
  }, [formData, checking, unique, errors, isEdit, editingProfile]);

 // Handle save
const handleSave = useCallback(async () => {
  if (!validateForm()) {
    showNotification("Validation Error", "Please fix all errors before submitting", "error");
    return;
  }

  try {
    setUploadingProfilePic(true);
    
    if (isEdit && editingProfile) {
      const formDataPayload = new FormData();

      formDataPayload.append("username", formData.userName.trim());
      formDataPayload.append("name", formData.name.trim());
      formDataPayload.append("email", formData.email.trim());
      formDataPayload.append("contact_number", formData.contact.trim());
      formDataPayload.append("user_role_id", formData.roleId);

      if (profilePicFile) {
        formDataPayload.append("profile_pic", profilePicFile);
      }

      // Ensure userId is a number
      const userId = Number(editingProfile.userId);
      
      if (isNaN(userId)) {
        showNotification("Error", "Invalid user ID", "error");
        return;
      }

      await callApi(
        "PUT",
        `/auth/user-with-profile/${userId}`, // Pass as number
        formDataPayload
      );

      showNotification("Success", "User profile updated successfully");
    } else {
      const formDataPayload = new FormData();
      
      formDataPayload.append('username', formData.userName.trim());
      formDataPayload.append('name', formData.name.trim());
      formDataPayload.append('email', formData.email.trim());
      formDataPayload.append('contact_number', formData.contact.trim());
      formDataPayload.append('user_role_id', formData.roleId);
      
      if (profilePicFile) {
        formDataPayload.append('profile_pic', profilePicFile);
      }

      await callApi("POST", "/auth/register-with-profile", formDataPayload);
      showNotification("Success", "User profile created successfully");
    }

    await fetchRoles();

    await fetchUserProfiles(
        selectedStatus.length ? selectedStatus[0].id : "",
        selectedRoleFilter.length ? selectedRoleFilter[0].text : "",
        true
);


    setIsModalOpen(false);
    resetForm();
  } catch (error) {
    const errorData = error?.response?.data?.data || error?.response?.data || {};
    const errorMessage = errorData.message || "Failed to save user profile";
    
    if (errorMessage.toLowerCase().includes("email") && 
        errorMessage.toLowerCase().includes("already exists")) {
      setUnique(prev => ({ ...prev, email: false }));
      setErrors(prev => ({ ...prev, email: "Email already exists" }));
      setTouched(prev => ({ ...prev, email: true }));
      showNotification("Validation Error", "Email already exists", "error");
    } else if (errorMessage.toLowerCase().includes("username") && 
               errorMessage.toLowerCase().includes("already exists")) {
      setUnique(prev => ({ ...prev, userName: false }));
      setErrors(prev => ({ ...prev, userName: "Username already exists" }));
      setTouched(prev => ({ ...prev, userName: true }));
      showNotification("Validation Error", "Username already exists", "error");
    } else {
      showNotification("Error", errorMessage, "error");
    }
  } finally {
    setUploadingProfilePic(false);
  }
}, [validateForm, isEdit, editingProfile, formData, profilePicFile, fetchRoles, fetchUserProfiles, resetForm, showNotification]);
  // Handle edit - FIXED: Removed checkUniqueField calls
  const handleEdit = useCallback((row) => {
    const profile = rows.find(r => r.id === row.id);
    if (profile) {
      setIsEdit(true);
      setEditingProfile(profile);
      setFormData({
        userName: profile.userName,
        name: profile.name,
        email: profile.email,
        contact: profile.contact,
        roleId: profile.roleId || "",
      });
      
      setTouched({
        userName: true,
        name: true,
        email: true,
        contact: true,
        roleId: true,
      });
      
      // Set all unique flags to true initially since these are the user's own values
      setUnique({
        userName: true,
        email: true,
        contact: true,
      });
      
      // Clear any existing errors
      setErrors({});
      
      if (profile.profilePic && profile.profilePic !== "-") {
        setProfilePicPreview(profile.profilePic);
      }
      
      setIsModalOpen(true);
    }
  }, [rows]);

  // Handle dropdown change
  const handleDropdownChange = useCallback(({ selectedItem }) => {
    const roleId = selectedItem ? selectedItem.id : "";
    
    setFormData(prev => ({
      ...prev,
      roleId,
    }));
    
    if (errors.roleId) {
      setErrors(prev => ({
        ...prev,
        roleId: ""
      }));
    }
    
    setTouched(prev => ({
      ...prev,
      roleId: true,
    }));
  }, [errors.roleId]);

  // Handle password change
const handlePasswordChange = useCallback((userId) => {
  const user = rows.find(r => r.id === userId);
  if (!user) return;

  setPasswordUser(user);
  setPasswordForm({
    username: user.userName,
    new_password: "",
    confirm_password: "",
  });
  setPasswordErrors({});
  setPasswordApiError("");
  setSameAsOldPasswordError(""); // <-- ADD THIS
  setIsPasswordModalOpen(true);
}, [rows]);

  // Handle password submit
const handlePasswordSubmit = useCallback(async () => {
  if (!validatePasswordForm()) return;

  try {
    setPasswordLoading(true);
    setPasswordApiError("");
    setSameAsOldPasswordError("");

    await callApi("POST", "/auth/change-password", {
      username: passwordForm.username,
      new_password: passwordForm.new_password,
      confirm_password: passwordForm.confirm_password,
    });

    showNotification("Success", "Password changed successfully");
    setIsPasswordModalOpen(false);
  } catch (error) {
    const responseData = error?.response?.data || {};
    const status = error?.response?.status || responseData?.status_code;

    const detail =
      responseData?.data?.detail ||   // "New password cannot be the same as old password"
      responseData?.detail ||
      "";

    const normalized = String(detail).toLowerCase();

    // ONLY this case: 400 + "same as old" → inline error below New Password
    if (status === 400 && normalized.includes("same as old")) {
      setSameAsOldPasswordError(
        "New password cannot be the same as old password"
      );
      setPasswordLoading(false);
      return;
    }

    // Everything else → keep your existing generic handling
    let errorMessage = "Failed to change password";
    if (status === 401) {
      errorMessage = "You are not authorized to change this password";
    } else if (status === 404) {
      errorMessage = "User not found";
    } else if (status >= 500) {
      errorMessage = "Server error. Please try again later";
    } else if (detail) {
      errorMessage = detail;
    }

    setPasswordApiError(errorMessage);
  } finally {
    setPasswordLoading(false);
  }
}, [validatePasswordForm, passwordForm, showNotification]);

  // Open delete confirmation
  const confirmDelete = useCallback((id) => {
    setDeleteProfileId(id);
    setIsDeleteModalOpen(true);
  }, []);

  // Handle delete
  const handleDelete = useCallback(async () => {
    const idsToDelete = batchDeleteIds.length > 0
      ? batchDeleteIds
      : deleteProfileId
      ? [deleteProfileId]
      : [];

    if (idsToDelete.length === 0) return;

    try {
      await Promise.all(
        idsToDelete.map(id =>
          callApi("DELETE", `/user-profiles/${id}`)
        )
      );

      showNotification("Success", `${idsToDelete.length} user profile(s) deleted successfully`);

     await fetchRoles();

await fetchUserProfiles(
  selectedStatus.length ? selectedStatus[0].id : "",
  selectedRoleFilter.length ? selectedRoleFilter[0].text : "",
  true
);
      setBatchDeleteIds([]);
    } catch {
      showNotification("Error", "Failed to delete some users", "error");
    } finally {
      setIsDeleteModalOpen(false);
      setDeleteProfileId(null);
    }
  }, [batchDeleteIds, deleteProfileId, fetchRoles, fetchUserProfiles, showNotification]);

  // Required label helper
  const requiredLabel = (text) => (
    <>
      {text} <span style={{ color: "#da1e28" }}>*</span>
    </>
  );

  // Role items for dropdown
  const roleItems = useMemo(() => roles, [roles]);

  // Get selected role
  const selectedRole = useMemo(() => 
    roleItems.find(item => item.id === formData.roleId) || null,
  [roleItems, formData.roleId]);

  // Paginated rows
  const paginatedRows = useMemo(() => 
    filteredRows.slice((page - 1) * pageSize, page * pageSize),
  [filteredRows, page, pageSize]);

  // Render action menu
  const renderActionMenu = useCallback((row) => {
    const handleActionClick = (action) => {
      switch (action) {
        case 'edit':
          handleEdit(row);
          break;
        case 'delete':
          confirmDelete(row.id);
          break;
        case 'password':
          handlePasswordChange(row.id);
          break;
        default:
          break;
      }
    };

    return (
      !isVerifier && (
      <OverflowMenu
        renderIcon={Settings}
        iconDescription="Actions"
        size="sm"
        flipped
      >
        
        <OverflowMenuItem
        
          itemText="Edit"
          onClick={() => handleActionClick('edit')}
        />
        
        <OverflowMenuItem
          itemText="Reset Password"
          onClick={() => handleActionClick('password')}
        />
      
        <OverflowMenuItem
          itemText="Delete"
          isDelete
          onClick={() => handleActionClick('delete')}
        />
      </OverflowMenu>
      )
    );
  }, [handleEdit, confirmDelete, handlePasswordChange]);

  // Render profile picture cell
  const renderProfilePicCell = (profilePic) => {
    if (!profilePic || profilePic === "-") {
      return (
        <div style={{ 
          width: "40px", 
          height: "40px", 
          borderRadius: "50%", 
          backgroundColor: "#f4f4f4",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>
          <UserAvatar size={20} style={{ fill: "#c6c6c6" }} />
        </div>
      );
    }
    
    return (
      <img 
        src={profilePic} 
        alt="Profile" 
        style={{ 
          width: "40px", 
          height: "40px", 
          borderRadius: "50%", 
          objectFit: "cover" 
        }}
        onError={(e) => {
          e.target.onerror = null;
          e.target.style.display = "none";
          e.target.parentNode.innerHTML = `<div style="width:40px;height:40px;border-radius:50%;background-color:#f4f4f4;display:flex;align-items:center;justify-content:center"><svg focusable="false" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 32 32" aria-hidden="true" style="fill:#c6c6c6"><path d="M16 2a14 14 0 1014 14A14 14 0 0016 2zm0 26a12 12 0 1112-12 12 12 0 01-12 12z"></path><path d="M16 10a6 6 0 106 6 6 6 0 00-6-6zm0 10a4 4 0 114-4 4 4 0 01-4 4z"></path></svg></div>`;
        }}
      />
    );
  };

  return (
    <div className="user-profiles-container">
      {/* Notification */}
      {notification.show && (
        <InlineNotification
          kind={notification.kind}
          title={notification.title}
          subtitle={notification.message}
          lowContrast
          style={{ marginBottom: "1rem" }}
          onClose={() => setNotification(prev => ({ ...prev, show: false }))}
        />
      )}

      {/* Loading State */}
      {isLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
          <InlineLoading description="Loading user profiles..." />
        </div>
      ) : (
        <>
<div
  style={{
    border: "1px solid #dcdcdc",
    borderRadius: "8px",
    padding: "16px",
    marginBottom: "24px",
    background: "#f4f4f4",
  }}
>
  <h3
    style={{
      margin: 0,
      marginBottom: "24px",
      fontSize: "28px",
      fontWeight: 400,
    }}
  >
    Filter Users
  </h3>

<div
  style={{
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(220px, 1fr))",
    gap: "16px 24px",
    alignItems: "end",
  }}
>
    <Dropdown
  id="status-filter"
  titleText="Status"
  label="Choose"
  items={statusList}
  itemToString={(item) => item?.text || ""}
  selectedItem={selectedStatus[0] || null}
  onChange={({ selectedItem }) =>
    setSelectedStatus(selectedItem ? [selectedItem] : [])
  }
/>

    <MultiSelect
      id="role-filter"
      titleText="Role"
      label="Choose"
      items={roles}
      itemToString={(item) => item?.text || ""}
      selectedItems={selectedRoleFilter}
      onChange={handleRoleChange}
    />

    {/* Buttons */}
      <div
        style={{
          display: "flex",
          gap: "12px",
          alignItems: "flex-end",
        }}
      >
        <Button
          kind="secondary"
          onClick={() => {
setSelectedStatus([]);
setSelectedRoleFilter([]);
setSearchTerm("");
setFilteredRows([]);
fetchUserProfiles("", "", true);
          }}
        >
          Clear
        </Button>

        <Button
          kind="primary"
          onClick={() => {
            fetchUserProfiles(
              selectedStatus.length ? selectedStatus[0].id : "",
              selectedRoleFilter.length ? selectedRoleFilter[0].text : "",
              true
            );
          }}
        >
          Search
        </Button>
      </div>

    </div> 
  </div>   

  <DataTable rows={paginatedRows} headers={headers}>
            {({ rows, headers, getHeaderProps, getRowProps, getSelectionProps, getBatchActionProps, selectedRows }) => (
              <TableContainer title="User Profiles">
                <TableToolbar>
                  <TableBatchActions {...getBatchActionProps()}>
                    <TableBatchAction
                      renderIcon={Delete}
                      onClick={() => {
                        if (selectedRows.length > 0) {
                          const ids = selectedRows.map(row => row.id);
                          setBatchDeleteIds(ids);
                          setIsDeleteModalOpen(true);
                        }
                      }}
                    >
                      Delete ({selectedRows.length})
                    </TableBatchAction>
                  </TableBatchActions>

                  <TableToolbarContent>
                    <TableToolbarSearch
                      persistent
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search user profiles..."
                    />
                    {!isVerifier &&(
                    <Button 
                      renderIcon={Add} 
                      kind="primary" 
                      onClick={handleAddNew}
                    >
                      Add New
                    </Button>
                    )}
                  </TableToolbarContent>
                </TableToolbar>

                <Table>
                  <TableHead>
                    <TableRow>
                      <th key="select" style={{ width: "48px" }} />
                     {headers.map((header) => {
                      const { key, ...headerProps } = getHeaderProps({ header });

                      return (
                        <TableHeader key={key} {...headerProps}>
                          {header.header}
                        </TableHeader>
                      );
                      })}
                      {!isVerifier &&<TableHeader key="actions-header">Actions</TableHeader>}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {rows.length > 0 ? (
                    rows.map((row) => {
  const originalRow = paginatedRows.find(
    (item) => item.id === row.id
  );

  const { key: rowKey, ...rowProps } = getRowProps({ row });
                      const { key: selectKey, ...selectProps } = getSelectionProps({ row });

                      return (
                        <TableRow key={rowKey} {...rowProps}>
                          <TableSelectRow key={selectKey} {...selectProps} />
                          {row.cells.map((cell) => {
  if (cell.info.header === "profilePic") {
    return (
      <TableCell key={`cell-${row.id}-${cell.id}`}>
        {renderProfilePicCell(cell.value)}
      </TableCell>
    );
  }

  if (cell.info.header === "name") {
    return (
      <TableCell key={`cell-${row.id}-${cell.id}`}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span>
            {cell.value === null || cell.value === undefined
              ? "-"
              : String(cell.value)}
          </span>

      {originalRow?.isGoogleProvider && (
  <span
    title="Signed in with Google"
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "22px",
      height: "22px",
      marginLeft: "6px",
      fontSize: "16px",
      fontWeight: "600",
      fontFamily: "Arial, sans-serif",
      background:
        "conic-gradient(from -45deg, #4285F4 0deg 90deg, #34A853 90deg 180deg, #FBBC05 180deg 270deg, #EA4335 270deg 360deg)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
      lineHeight: "1",
    }}
  >
    G
  </span>
)}
        </div>
      </TableCell>
    );
  }

  return (
    <TableCell key={`cell-${row.id}-${cell.id}`}>
      {cell.value === null || cell.value === undefined
        ? "-"
        : String(cell.value)}
    </TableCell>
  );
})}
                          {!isVerifier &&
                          (<TableCell key={`actions-${row.id}`}>
                            {renderActionMenu(row)}
                          </TableCell>
                        )}
                        </TableRow>
                      );
                    })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={headers.length + 2} style={{ textAlign: "center", padding: "2rem" }}>
                          {searchTerm ? "No matching user profiles found" : "No user profiles found"}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DataTable>

          {/* Pagination */}
          {filteredRows.length > 0 && (
            <Pagination
              totalItems={filteredRows.length}
              page={page}
              pageSize={pageSize}
              pageSizes={[5, 10, 20, 50]}
              onChange={({ page, pageSize }) => { 
                setPage(page); 
                setPageSize(pageSize); 
              }}
            />
          )}
        </>
      )}

      {/* Add/Edit Modal */}
      <Modal
        open={isModalOpen}
        modalHeading={isEdit ? "Edit User Profile" : "Add User Profile"}
        primaryButtonText={isEdit ? "Update" : "Save"}
        secondaryButtonText="Cancel"
        onRequestClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        onRequestSubmit={handleSave}
        preventCloseOnClickOutside
        primaryButtonDisabled={!isFormValid || uploadingProfilePic}
      >
        <Form style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {/* Profile Picture Upload - Only for new users */}
          {!isEdit && (
            <div style={{ marginBottom: "1rem" }}>
              <p style={{ 
                fontSize: "0.875rem", 
                fontWeight: "600", 
                marginBottom: "0.5rem",
                color: "#161616"
              }}>
                Profile Picture
              </p>
              
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                flexWrap: "wrap"
              }}>
                {/* Preview area */}
                <div style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  backgroundColor: "#f4f4f4",
                  border: "2px dashed #c6c6c6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden"
                }}>
                  {profilePicPreview ? (
                    <img 
                      src={profilePicPreview} 
                      alt="Profile preview" 
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover"
                      }}
                    />
                  ) : (
                    <UserAvatar size={32} style={{ fill: "#c6c6c6" }} />
                  )}
                </div>
                
                {/* Upload controls */}
                <div style={{ flex: 1 }}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    id="profile-pic-upload"
                    accept={ACCEPTED_IMAGE_TYPES}
                    onChange={handleProfilePicChange}
                    style={{ display: "none" }}
                  />
                  
                  <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    <Button
                      kind="secondary"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingProfilePic}
                      renderIcon={Upload}
                    >
                      Choose Image
                    </Button>
                    
                    {profilePicFile && (
                      <Button
                        kind="danger"
                        size="sm"
                        onClick={clearProfilePic}
                        disabled={uploadingProfilePic}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                  
                  <p style={{
                    fontSize: "0.75rem",
                    color: "#6f6f6f",
                    marginTop: "0.25rem"
                  }}>
                    JPG, JPEG, PNG, GIF, WEBP (Max {MAX_PROFILE_PIC_SIZE_MB}MB)
                  </p>
                  
                  {errors.profilePic && (
                    <p style={{
                      fontSize: "0.75rem",
                      color: "#da1e28",
                      marginTop: "0.25rem"
                    }}>
                      {errors.profilePic}
                    </p>
                  )}
                  
                  {profilePicFile && (
                    <p style={{
                      fontSize: "0.75rem",
                      color: "#0f62fe",
                      marginTop: "0.25rem"
                    }}>
                      Selected: {profilePicFile.name} ({(profilePicFile.size / 1024 / 1024).toFixed(2)} MB)
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Show existing profile pic in edit mode */}
          {isEdit && profilePicPreview && (
            <div style={{ marginBottom: "1rem" }}>
              <p style={{ 
                fontSize: "0.875rem", 
                fontWeight: "600", 
                marginBottom: "0.5rem",
                color: "#161616"
              }}>
                Profile Picture
              </p>
              <div style={{
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                overflow: "hidden",
                border: "2px solid #e0e0e0"
              }}>
                <img 
                  src={profilePicPreview} 
                  alt="Profile" 
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover"
                  }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "";
                    e.target.style.display = "none";
                    e.target.parentNode.innerHTML = `<div style="width:80px;height:80px;border-radius:50%;background-color:#f4f4f4;display:flex;align-items:center;justify-content:center"><svg focusable="false" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" aria-hidden="true" style="fill:#c6c6c6"><path d="M16 2a14 14 0 1014 14A14 14 0 0016 2zm0 26a12 12 0 1112-12 12 12 0 01-12 12z"></path><path d="M16 10a6 6 0 106 6 6 6 0 00-6-6zm0 10a4 4 0 114-4 4 4 0 01-4 4z"></path></svg></div>`;
                  }}
                />
              </div>
              <p style={{ fontSize: "0.75rem", color: "#6f6f6f", marginTop: "0.25rem" }}>
                To change profile picture, use edit in next version
              </p>
            </div>
          )}

          {/* Name Field */}
          <TextInput
            id="name"
            labelText={requiredLabel("Name")}
            value={formData.name}
            onChange={handleInputChange}
            onBlur={() => handleBlur("name")}
            placeholder="Enter full name"
            invalid={touched.name && !!errors.name}
            invalidText={errors.name}
            disabled={uploadingProfilePic}
          />

          {/* Username Field */}
          <div>
            <TextInput
              id="userName"
              labelText={requiredLabel("Username")}
              value={formData.userName}
              onChange={handleInputChange}
              onBlur={() => handleBlur("userName")}
              placeholder={`Enter username (min. ${USERNAME_MIN_LENGTH} chars)`}
              invalid={touched.userName && (!!errors.userName || (!isEdit && !unique.userName && !checking.userName))}
              invalidText={errors.userName || (!isEdit && !unique.userName && !checking.userName ? "Username already exists" : "")}
              disabled={isEdit || uploadingProfilePic}
            />
            {checking.userName && (
              <div style={{ marginTop: "0.25rem" }}>
                <InlineLoading description="Checking username availability..." />
              </div>
            )}
            {isEdit && (
              <p style={{ fontSize: "0.75rem", color: "#6f6f6f", marginTop: "0.25rem" }}>
                Username cannot be changed for existing users
              </p>
            )}
          </div>

          {/* Email Field */}
          <div>
            <TextInput
              id="email"
              labelText={requiredLabel("Email")}
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              onBlur={() => handleBlur("email")}
              placeholder="Enter email address"
              invalid={touched.email && (!!errors.email || (
                isEdit && editingProfile 
                  ? (formData.email.trim().toLowerCase() !== editingProfile.email?.toLowerCase() && !unique.email && !checking.email)
                  : (!unique.email && !checking.email)
              ))}
              invalidText={errors.email || (
                isEdit && editingProfile
                  ? (formData.email.trim().toLowerCase() !== editingProfile.email?.toLowerCase() && !unique.email && !checking.email ? "Email already exists" : "")
                  : (!unique.email && !checking.email ? "Email already exists" : "")
              )}
              disabled={uploadingProfilePic}
            />
            {checking.email && (
              <div style={{ marginTop: "0.25rem" }}>
                <InlineLoading description="Checking email availability..." />
              </div>
            )}
          </div>

          {/* Contact Field */}
          <div>
            <TextInput
              id="contact"
              labelText={requiredLabel("Contact Number")}
              value={formData.contact}
              onChange={handleInputChange}
              onBlur={() => handleBlur("contact")}
              placeholder="Enter contact number"
              invalid={touched.contact && (!!errors.contact || (
                isEdit && editingProfile 
                  ? (formData.contact.trim().toLowerCase() !== editingProfile.contact?.toLowerCase() && !unique.contact && !checking.contact)
                  : (!unique.contact && !checking.contact)
              ))}
              invalidText={errors.contact || (
                isEdit && editingProfile
                  ? (formData.contact.trim().toLowerCase() !== editingProfile.contact?.toLowerCase() && !unique.contact && !checking.contact ? "Contact number already exists" : "")
                  : (!unique.contact && !checking.contact ? "Contact number already exists" : "")
              )}
              disabled={uploadingProfilePic}
            />
            {checking.contact && (
              <div style={{ marginTop: "0.25rem" }}>
                <InlineLoading description="Checking contact availability..." />
              </div>
            )}
          </div>

          {/* Role Dropdown */}
          <Dropdown
  id="role"
  titleText="Role"
  label="Select a role"
  items={roleItems}
  itemToString={(item) => item?.text || ""}
  selectedItem={selectedRole}
  direction="top"
  onChange={({ selectedItem }) => {
    setFormData((prev) => ({
      ...prev,
      roleId: selectedItem?.id || null,
    }));
  }}
/>
          
          {loadingRoles && (
            <InlineLoading description="Loading roles..." />
          )}
        </Form>
      </Modal>

      {/* Reset Password Modal */}
<Modal
  open={isPasswordModalOpen}
  modalHeading={`Reset Password — ${passwordUser?.userName || ""}`}
  primaryButtonText={passwordLoading ? "Updating..." : "Change Password"}
  secondaryButtonText="Cancel"
  onRequestClose={() => {
    setIsPasswordModalOpen(false);
    setSameAsOldPasswordError(""); // Clear on close
  }}
  onRequestSubmit={handlePasswordSubmit}
  primaryButtonDisabled={
    passwordLoading ||
    !isPasswordStrong(passwordForm.new_password) ||
    passwordForm.new_password !== passwordForm.confirm_password
  }
  preventCloseOnClickOutside
>
  <Form style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
<div>
  <TextInput
    id="new_password"
    type="password"
    labelText="New Password *"
    value={passwordForm.new_password}
    onChange={handlePasswordInputChange}
    invalid={!!passwordErrors.new_password}
    invalidText={passwordErrors.new_password}
    disabled={passwordLoading}
  />
  {sameAsOldPasswordError && (
    <p style={{ fontSize: "0.75rem", color: "#da1e28", marginTop: "0.25rem" }}>
      {sameAsOldPasswordError}
    </p>
  )}
</div>

    <TextInput
      id="confirm_password"
      type="password"
      labelText="Confirm Password *"
      value={passwordForm.confirm_password}
      onChange={handlePasswordInputChange}
      invalid={!!passwordErrors.confirm_password}
      invalidText={passwordErrors.confirm_password}
      disabled={passwordLoading}
    />

    <p style={{ fontSize: "0.875rem", color: "#6f6f6f" }}>
      Password must include uppercase, lowercase, number, special character and be at least 8 characters.
    </p>

    {passwordApiError && (
      <InlineNotification
        kind="error"
        title="Error"
        subtitle={passwordApiError}
        lowContrast
        onClose={() => setPasswordApiError("")}
      />
    )}

    {passwordLoading && (
      <InlineLoading description="Updating password..." />
    )}
  </Form>
</Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={isDeleteModalOpen}
        modalHeading="Confirm Delete"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        danger
        onRequestClose={() => setIsDeleteModalOpen(false)}
        onRequestSubmit={handleDelete}
      >
        <p>
          {batchDeleteIds.length > 0
            ? `Are you sure you want to delete ${batchDeleteIds.length} selected users? This action cannot be undone.`
            : "Are you sure you want to delete this user profile? This action cannot be undone."}
        </p>
      </Modal>
    </div>
  );
}